import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { FAMILY_IMAGE } from "@/lib/data";

const inputCls =
  "h-12 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal focus-visible:ring-2 focus-visible:ring-royal/30";

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  const sendReset = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail, origin_url: window.location.origin });
      setForgotSent(true);
    } catch {
      toast.error("Couldn't send that right now — try again in a moment");
    } finally {
      setForgotBusy(false);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";
  const destFor = (u) => (u?.role === "admin" && from === "/account" ? "/admin" : from);

  useEffect(() => {
    if (user) navigate(destFor(user), { replace: true });
  }, [user, from, navigate]);

  if (user) return <Navigate to={destFor(user)} replace />;

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res =
      mode === "login" ? await login(email, password) : await register(name, email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back." : "Account created. Welcome in.");
      if (mode === "login" && res.user?.returning_discount) {
        setTimeout(() => toast.success("Your 10% returning-customer discount is on — it applies automatically at checkout."), 600);
      }
      navigate(destFor(res.user), { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <main className="grid min-h-[calc(100vh-5rem)] bg-white lg:grid-cols-2">
      <div className="flex items-center justify-center px-4 py-16 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <LogoMark className="h-[168px]" />
          <h1 className="mt-7 font-display text-3xl font-extrabold tracking-tight text-ink">
            {mode === "login" ? "Welcome back." : "Join Oregon Tote Rentals."}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {mode === "login"
              ? "Sign in to book packages and track your rentals."
              : "Create an account to lock in your move in minutes."}
          </p>
          <div className="mt-7 grid grid-cols-2 gap-2 rounded-2xl bg-paper p-1.5">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); }}
                data-testid="auth-mode-toggle"
                className={`rounded-xl py-2.5 text-sm font-semibold transition-all duration-200 ${
                  mode === m ? "bg-white text-ink shadow-md" : "text-slate-500 hover:text-ink"
                }`}
              >
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={googleSignIn}
            data-testid="google-signin-button"
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-3.5 text-sm font-semibold text-ink transition-all duration-200 hover:border-royal hover:shadow-md hover:shadow-royal/10"
          >
            <svg className="h-4.5 w-4.5" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81Z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24Z"/>
              <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1Z"/>
              <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77Z"/>
            </svg>
            Continue with Google
          </button>
          <div className="mt-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">or with email</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>
          <form onSubmit={submit} className="mt-5 space-y-4">
            {mode === "register" && (
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                data-testid="auth-name-input"
                className={inputCls}
                required
              />
            )}
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              type="email"
              data-testid="auth-email-input"
              className={inputCls}
              required
            />
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "register" ? "Password (6+ characters)" : "Password"}
              type="password"
              data-testid="auth-password-input"
              className={inputCls}
              required
            />
            {error && (
              <p data-testid="auth-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-semibold text-red-600">
                {error}
              </p>
            )}
            {mode === "login" && (
              <div>
                {!forgotOpen ? (
                  <button
                    type="button"
                    onClick={() => { setForgotOpen(true); setForgotEmail(email); }}
                    data-testid="forgot-password-toggle"
                    className="text-xs font-semibold text-slate-400 transition-colors duration-200 hover:text-royal"
                  >
                    Forgot your password?
                  </button>
                ) : forgotSent ? (
                  <p className="rounded-xl bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-700" data-testid="forgot-sent-message">
                    If that email has an account, a reset link is on its way — check your inbox (good for 1 hour).
                  </p>
                ) : (
                  <div className="flex gap-2" data-testid="forgot-password-form">
                    <input
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendReset(e); } }}
                      type="email"
                      placeholder="Your account email"
                      data-testid="forgot-email-input"
                      className={inputCls}
                      required
                    />
                    <button
                      type="button"
                      onClick={sendReset}
                      disabled={forgotBusy}
                      data-testid="forgot-send-button"
                      className="shrink-0 rounded-xl bg-ink px-4 text-xs font-semibold text-white transition-colors duration-200 hover:bg-royal disabled:opacity-50"
                    >
                      {forgotBusy ? "Sending…" : "Email me a link"}
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-royal py-4 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.01] hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25 active:scale-[0.98] disabled:opacity-50"
            >
              {busy ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </motion.div>
      </div>
      <div className="relative hidden overflow-hidden lg:block">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          src={FAMILY_IMAGE}
          alt="Family packing clothes into an Oregon Tote Rentals tote"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
        <div className="absolute bottom-14 left-12 right-12">
          <p className="font-serif text-3xl italic leading-snug text-white sm:text-4xl">
            "Fifty totes dropped off Tuesday, picked up the next week. Easiest move of my life."
          </p>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-white/70">
            Jess M. — Old Mill District, Bend
          </p>
        </div>
      </div>
    </main>
  );
}
